import React from 'react';
import twMerge from 'twMerge';

import Image from 'molecules/Image';
import Section from 'molecules/Section';

import getTheme from 'utils/getTheme';
import richTextParser from 'utils/richTextParser/richTextParser';

import type { ImageData } from 'molecules/Image';
import type { SectionProps } from 'molecules/Section';
import type { RichText } from 'utils/richTextParser/richTextParser';

export type TrowelProps = {
  author?: {
    fullName?: string;
    thumbnail?: { file?: { url?: string } };
    position?: string;
  };
  background?: SectionProps['background'];
  cards?: { heading?: string; icon?: { file?: { url?: string } } }[];
  padding?: string;
  quote?: { raw };
  quoteRight?: boolean;
  sectionIdLink?: string;
};

const Trowel = ({ author, background, cards, padding, quote, quoteRight, sectionIdLink }: TrowelProps) => {
  const { fullName, bio, headshot, position } = author || {};
  const theme = getTheme(background || 'white');

  return (
    <Section id={sectionIdLink} background={background} padding={padding} contained={false}>
      <div
        className={twMerge(
          'trowel mt-0 flex w-full flex-col justify-between gap-16 lg:gap-32 items-center',
          !quoteRight ? 'lg:flex-row-reverse' : 'lg:flex-row',
          theme === 'dark' ? '[&>p]:text-white text-white' : '[&>p]:text-gray-900 text-gray-900',
        )}
      >
        <div className="trowel-quote-container h-fit w-full max-w-full lg:max-w-[40%]">
          {quote && (
            <blockquote className="relative">
              <div
                className={twMerge(
                  'trowel-quote text-xl [&>p]:inline',
                  theme === 'dark' ? '[&>p]:text-white text-white' : '[&>p]:text-gray-900 text-gray-900',
                )}
              >
                <span className="-ml-3 mt-1.5 text-2xl leading-[90%]">“</span>
                {richTextParser(quote as RichText)}
                <span className="text-2xl leading-[28px]">”</span>
              </div>
            </blockquote>
          )}
          {(headshot || bio || position) && (
            <div className="trowel-author mt-10 flex items-center">
              {headshot && (
                <div className="trowel-headshot-container headshot size-[60px] overflow-hidden rounded-[50%]">
                  <Image
                    image={headshot as ImageData}
                    alt={headshot?.title || ''}
                    className="trowel-headshot size-full object-cover"
                  />
                </div>
              )}
              {(fullName || position) && (
                <div className="trowel-author ml-5 flex flex-col">
                  {fullName && <p className="trowel-name !mb-1">{fullName}</p>}
                  {position && <p className="trowel-position">{position}</p>}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="trowel-items flex h-fit w-full max-w-full flex-wrap gap-8 lg:max-w-[60%] lg:gap-14">
          {cards?.map(({ heading, icon }, index) => (
            <div className="trowel-item w-[calc(50%-1rem)] lg:w-[calc(50%-1.75rem)]" key={index}>
              {icon && (
                <div className="trowel-item-icon-container flex size-[72px]">
                  <Image
                    image={icon as ImageData}
                    alt={icon?.title || ''}
                    className="trowel-item-icon size-full object-contain object-bottom"
                  />
                </div>
              )}
              {heading && <p className="trowel-item-heading mt-4 flex items-end text-xl">{heading}</p>}
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};

export default Trowel;
